import React, {useEffect, useRef, useState} from 'react';
import {
    Button,
    Flex,
    Input,
    PriorityIcon,
    Select,
    Text
} from '@priority-software/priority-style-react';
import styles from '../styles.module.scss';
import {Footer} from './Footer';
import {procClientContinue} from '../../../_shared/GWTBridge/invoke/procMain';

export const BankTransferForm = ({
    total,
    token,
    setIsLoading,
    isSubmitted,
    setIsSubmitted,
    setIsError,
    currency
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [mobile, setMobile] = useState('');
    const [email, setEmail] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [accountType, setAccountType] = useState('');
    const [isAccountNumberInvalid, setIsAccountNumberInvalid] = useState(false);
    const isPayment = useRef(total !== '0');
    const data = useRef({firstName: '', lastName: '', mobile: '', email: ''});

    useEffect(() => {
        setupSecuredPaymentCollector();
    }, []);

    const setupSecuredPaymentCollector = () => {
        (window as any).bluesnap.securedPaymentCollectorSetup(
            token,
            function (sdkResponse) {
                if (sdkResponse.code == 1) {
                    // Data submission was a success
                    // Continue form submission to your server & complete the transaction
                    setIsLoading(true);
                    submitPayment();
                } else {
                    // Data submission resulted in error or warning
                    const {errors, warnings} = sdkResponse.info;
                    console.log('Errors: ', errors);
                    console.log('Warnings: ', warnings);
                    setIsSubmitted(false);
                    setIsError(true);
                }
            },
            false
        );
    };

    const isPaymentButtonDisabled = () => {
        return (
            !firstName ||
            !lastName ||
            !email ||
            !routingNumber ||
            !accountNumber ||
            !accountType ||
            isAccountNumberInvalid ||
            isSubmitted
        );
    };

    const accountTypes = [
        {
            label: 'Personal checking account',
            value: 'CONSUMER_CHECKING'
        },
        {
            label: 'Personal savings account',
            value: 'CONSUMER_SAVINGS'
        },
        {
            label: 'Business checking account',
            value: 'CORPORATE_CHECKING'
        },
        {
            label: 'Business savings account',
            value: 'CORPORATE_SAVINGS'
        }
    ];

    const clearBankAccount = () => {
        setAccountNumber('');
    };

    const validateAccountNumber = () => {
        const isNumber = /^\d+$/.test(accountNumber);
        setIsAccountNumberInvalid(!isNumber || accountNumber.length < 4);
    };

    const submitPayment = () => {
        procClientContinue(
            data.current.firstName +
                '\n' +
                data.current.lastName +
                '\n' +
                '\n' +
                data.current.email +
                '\n' +
                data.current.mobile +
                '\n'
        );
    };

    const submitData = () => {
        const data = {
            amount: Number(total.split(' ')[0]),
            currency: currency
        };
        setIsSubmitted(true);
        (window as any).bluesnap.securedPaymentCollectorSubmitData(data);
    };

    const updateFirstName = value => {
        data.current.firstName = value;
        setFirstName(value);
    };

    const updateLastName = value => {
        data.current.lastName = value;
        setLastName(value);
    };

    const updateMobile = value => {
        data.current.mobile = value;
        setMobile(value);
    };

    const updateEmail = value => {
        data.current.email = value;
        setEmail(value);
    };

    return (
        <div>
            <Flex direction="column" componentClasses={styles.formRow}>
                <Input
                    placeholder=""
                    label="Account number"
                    value={accountNumber}
                    onChange={e => setAccountNumber(e.target.value)}
                    size="large"
                    maxLength={17}
                    onBlur={validateAccountNumber}
                >
                    <Button
                        icon={PriorityIcon.closeSmall}
                        roundedCorners={false}
                        skin="text"
                        componentClasses={styles.clearBtn}
                        onClick={clearBankAccount}
                    />
                </Input>
                {isAccountNumberInvalid && (
                    <Text componentClasses={styles.invalidLabel}>
                        {!accountNumber.length ? 'Empty' : 'Invalid'}
                    </Text>
                )}
                <input
                    id="ecpAccountNumber"
                    readOnly
                    name="ecpAccountNumber"
                    data-bluesnap="ecpAccountNumber"
                    value={accountNumber}
                    className={styles.invisibleInput}
                />
            </Flex>
            <Flex justifyContent="space-between" componentClasses={styles.formRow}>
                <Input
                    placeholder="123456789"
                    label="Routing number"
                    value={routingNumber}
                    onChange={e => setRoutingNumber(e.target.value)}
                    componentClasses={styles.halfRow}
                    size="large"
                />
                <input
                    value={routingNumber}
                    id="ecpRoutingNumber"
                    readOnly
                    name="ecpRoutingNumber"
                    data-bluesnap="ecpRoutingNumber"
                    placeholder=""
                    className={styles.invisibleInput}
                />
                <Flex
                    direction="column"
                    componentClasses={styles.halfRow}
                    justifyContent="flex-start"
                >
                    <Text componentClasses={styles.label}>Account type</Text>
                    <input
                        id="ecpAccountType"
                        name="ecpAccountType"
                        readOnly
                        data-bluesnap="ecpAccountType"
                        value={accountType}
                        className={styles.invisibleInput}
                    />
                    <Select
                        options={accountTypes}
                        placeholder="Choose a type…"
                        componentClasses={styles.select}
                        size="large"
                        isSearchable={false}
                        onChange={op => setAccountType(op.value)}
                    />
                </Flex>
            </Flex>
            <Flex justifyContent="space-between" componentClasses={styles.formRow}>
                <Input
                    placeholder="Enter your name…"
                    label="First name"
                    value={firstName}
                    onChange={e => updateFirstName(e.target.value)}
                    componentClasses={styles.halfRow}
                    size="large"
                />
                <Input
                    placeholder="Enter your last name…"
                    label="Last name"
                    value={lastName}
                    onChange={e => updateLastName(e.target.value)}
                    componentClasses={styles.halfRow}
                    size="large"
                />
            </Flex>
            <Flex justifyContent="space-between" componentClasses={styles.formRow}>
                <Input
                    placeholder="(123) 123-1234"
                    label="Cell Phone No."
                    value={mobile}
                    onChange={e => updateMobile(e.target.value)}
                    componentClasses={styles.halfRow}
                    size="large"
                />
                <Input
                    placeholder="email@youremail.com"
                    label="Email"
                    value={email}
                    onChange={e => updateEmail(e.target.value)}
                    componentClasses={styles.halfRow}
                    size="large"
                />
            </Flex>
            <Footer
                total={total}
                isPayment={isPayment.current}
                submitPayment={submitData}
                isPaymentButtonDisabled={isPaymentButtonDisabled()}
                isBankTransfer={true}
            />
        </div>
    );
};
